import React, { useState, useEffect } from 'react';
import {
  Box, Heading, Text, Select, Flex, Divider, Input, FormControl, FormLabel, useToast, Button, Table, Thead, Tbody, Tr, Th, Td, Tag, Spinner, IconButton, Checkbox, useBreakpointValue, Accordion, AccordionItem, AccordionButton, AccordionPanel, AccordionIcon, AlertDialog, AlertDialogBody, AlertDialogFooter, AlertDialogHeader, AlertDialogContent, AlertDialogOverlay,
  Drawer, DrawerOverlay, DrawerContent, DrawerHeader, DrawerBody, DrawerFooter, DrawerCloseButton, useDisclosure, Stack
} from '@chakra-ui/react';
import { useAuth } from '../hooks/useAuth';
import { api } from '../services/api';
import { labelTipo, corTipo, corTextoTipo, isEntrada, descricaoCurta } from '../utils/lancamentos';
import { EditIcon, DeleteIcon, CheckIcon, CloseIcon } from '@chakra-ui/icons';

const meses = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro'
];

function formatarMoeda(valor: number) {
  return valor.toLocaleString('pt-BR', { minimumFractionDigits: 2 });
}

function formatarMoedaInput(valor: string | number) {
  let v = '';
  if (typeof valor === 'number') {
    v = Math.round(valor * 100).toString();
  } else {
    v = valor.replace(/\D/g, '');
  }
  if (!v) return '0,00';
  // Garante ao menos 3 dígitos para separar centavos (0.05 → '5' → '0,05')
  while (v.length < 3) v = '0' + v;
  const centavos = v.slice(-2);
  let reais = v.slice(0, -2);
  reais = reais.replace(/^0+/, '') || '0';
  reais = reais.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return `${reais},${centavos}`;
}

function moedaParaNumero(valor: string) {
  return Number(valor.replace(/\./g, '').replace(',', '.'));
}

// Função utilitária para formatar data yyyy-mm-dd para dd/mm/yyyy
function formatarDataBR(dataStr: string) {
  if (!dataStr) return '';
  // Aceita 'YYYY-MM-DD' e também ISO com hora, cortando o que vem depois
  const [ano, mes, dia] = dataStr.slice(0, 10).split('-');
  return `${dia}/${mes}/${ano}`;
}

// Data de hoje no fuso local (toISOString usa UTC e vira o dia seguinte à noite)
function dataHojeLocal() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

// Extrai "i/n" da parcela: usa colunas se existirem, senão a descrição "Parcela i/n"
function getParcelaInfo(lanc: any) {
  if (lanc?.parcela && lanc?.num_parcelas) return `${lanc.parcela}/${lanc.num_parcelas}`;
  const m = (lanc?.descricao || '').match(/Parcela (\d+)\/(\d+)/i);
  return m ? `${m[1]}/${m[2]}` : '-';
}

const DIAS_SEMANA = ['Dom', 'Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb'];

// "Qua, 24/09" para o cabeçalho de cada dia na lista do celular
function cabecalhoDia(iso: string) {
  const [a, m, d] = iso.slice(0, 10).split('-').map(Number);
  return `${DIAS_SEMANA[new Date(a, m - 1, d).getDay()]}, ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}`;
}

const Lancamentos: React.FC = () => {
  const { user } = useAuth();
  const hoje = new Date();
  const [mes, setMes] = useState(hoje.getMonth());
  const [ano, setAno] = useState(hoje.getFullYear());
  const [categorias, setCategorias] = useState<any[]>([]);
  const [tipoLancamento, setTipoLancamento] = useState<'receita' | 'despesa' | 'transferencia'>('despesa');
  const [categoriaId, setCategoriaId] = useState('');
  const [valor, setValor] = useState('');
  const [data, setData] = useState(() => dataHojeLocal());
  const [descricao, setDescricao] = useState('');
  const [lancamentos, setLancamentos] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const toast = useToast();
  const [editId, setEditId] = useState<string | null>(null);
  const [editLanc, setEditLanc] = useState<any>(null);
  const [cartoes, setCartoes] = useState<any[]>([]);
  const [usarCartao, setUsarCartao] = useState(false);
  const [cartaoId, setCartaoId] = useState('');
  const [parcelado, setParcelado] = useState(false);
  const [numParcelas, setNumParcelas] = useState(2);
  const [bancos, setBancos] = useState<any[]>([]);
  const [bancoId, setBancoId] = useState('');
  const [bancoDestinoId, setBancoDestinoId] = useState('');
  const isMobile = useBreakpointValue({ base: true, md: false });
  // Seleção para edição em lote (revisão em massa após sincronizações grandes)
  const [selecionados, setSelecionados] = useState<string[]>([]);
  const [loteCategoria, setLoteCategoria] = useState('');
  const [loteTipo, setLoteTipo] = useState('');
  const [loteConta, setLoteConta] = useState('');
  // Filtros da lista (agrupar semelhantes acelera a revisão em lote)
  const [filtroRevisar, setFiltroRevisar] = useState(false);
  const [filtroTexto, setFiltroTexto] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroCategoria, setFiltroCategoria] = useState('');
  const [filtroConta, setFiltroConta] = useState('');
  const [filtroDataIni, setFiltroDataIni] = useState('');
  const [filtroDataFim, setFiltroDataFim] = useState('');
  const [filtroSemPar, setFiltroSemPar] = useState(false);
  // Vincular um lançamento existente à baixa de uma conta fixa
  const [contasFixas, setContasFixas] = useState<any[]>([]);
  const [pagamentosContas, setPagamentosContas] = useState<any[]>([]);
  const [vincularLanc, setVincularLanc] = useState<any>(null);
  const [vincularContaId, setVincularContaId] = useState('');
  const [parcelarLanc, setParcelarLanc] = useState<any>(null);
  const [parcelarTotal, setParcelarTotal] = useState(2);
  const [parcelarNumero, setParcelarNumero] = useState(1);
  // Ordenação clicável: ordenar por descrição agrupa semelhantes
  const [ordenarPor, setOrdenarPor] = useState<'data' | 'valor' | 'categoria' | 'descricao'>('data');
  const [ordemAsc, setOrdemAsc] = useState(false);
  const [deleteId, setDeleteId] = useState<string | null>(null);
  const filtrosMobile = useDisclosure();
  const cancelRef = React.useRef<any>();

  // Buscar categorias, cartões e bancos
  useEffect(() => {
    if (!user) return;
    const fetchAux = async () => {
      setLoading(true);
      try {
        const [cats, carts, bcs, cps, pgs] = await Promise.all([
          api.get('/api/categorias'),
          api.get('/api/cartoes'),
          api.get('/api/bancos'),
          api.get('/api/contas-pagar'),
          api.get(`/api/contas-pagar/pagamentos?desde_ano=${new Date().getFullYear() - 1}`),
        ]);
        setCategorias(cats);
        setCartoes(carts);
        setBancos(bcs);
        setContasFixas(cps);
        setPagamentosContas(pgs);
      } catch {
        // sessão expirada: o PrivateRoute redireciona
      }
      setLoading(false);
    };
    fetchAux();
  }, [user]);

  // Buscar lançamentos do mês/ano
  const fetchLancamentos = React.useCallback(async () => {
    if (!user) return;
    try {
      // Com período informado, busca pela faixa de datas (pode atravessar
      // meses); sem período, o mês selecionado
      const query = (filtroDataIni || filtroDataFim)
        ? `data_inicio=${filtroDataIni || '1900-01-01'}&data_fim=${filtroDataFim || '2999-12-31'}&limit=1000`
        : `mes=${mes}&ano=${ano}`;
      setLancamentos(await api.get(`/api/lancamentos?${query}`));
    } catch {
      setLancamentos([]);
    }
  }, [user, mes, ano, filtroDataIni, filtroDataFim]);

  useEffect(() => {
    setLoading(true);
    fetchLancamentos().finally(() => setLoading(false));
  }, [fetchLancamentos]);

  // Filtrar categorias ativas pelo tipo selecionado
  const categoriasFiltradas = categorias.filter(cat => cat.tipo_lancamento === tipoLancamento && cat.ativo !== false);

  // Verificar se é transferência entre bancos
  const isTransferencia = tipoLancamento === 'transferencia';

  // Trocar o tipo do lançamento limpando estados que só valem para despesa,
  // senão a validação trava em campos que não estão mais visíveis
  const handleTipoChange = (novoTipo: 'receita' | 'despesa' | 'transferencia') => {
    setTipoLancamento(novoTipo);
    setCategoriaId('');
    if (novoTipo !== 'despesa') {
      setUsarCartao(false);
      setCartaoId('');
      setParcelado(false);
    }
  };

  // Salvar lançamento
  const handleSalvar = async () => {
    if (!user) return;
    if (!valor || !data) {
      toast({ title: 'Preencha todos os campos obrigatórios', status: 'warning' });
      return;
    }
    if (isTransferencia) {
      if (!bancoId || !bancoDestinoId) {
        toast({ title: 'Selecione os bancos de origem e destino', status: 'warning' });
        return;
      }
      if (bancoId === bancoDestinoId) {
        toast({ title: 'Os bancos de origem e destino devem ser diferentes', status: 'warning' });
        return;
      }
    } else {
      if (!categoriaId) {
        toast({ title: 'Selecione a categoria', status: 'warning' });
        return;
      }
      if (usarCartao) {
        if (!cartaoId) {
          toast({ title: 'Selecione o cartão', status: 'warning' });
          return;
        }
      } else {
        if (!bancos.length || !bancoId) {
          toast({ title: 'Selecione o banco', status: 'warning' });
          return;
        }
      }
    }
    if (parcelado && (!numParcelas || numParcelas < 2)) {
      toast({ title: 'Informe o número de parcelas (mínimo 2)', status: 'warning' });
      return;
    }
    const valorNum = moedaParaNumero(valor);
    if (isNaN(valorNum) || valorNum <= 0) {
      toast({ title: 'Valor inválido', status: 'error' });
      return;
    }
    setLoading(true);
    // mes/ano são derivados da data pelo servidor; aqui só montamos os registros
    const lancs = [];
    if (parcelado && tipoLancamento === 'despesa') {
      // Parcelado: criar vários lançamentos
      const [anoStr, mesStr, diaStr] = data.split('-');
      // Divide em centavos para a soma das parcelas bater exatamente com o total
      const totalCentavos = Math.round(valorNum * 100);
      const baseCentavos = Math.floor(totalCentavos / numParcelas);
      const restoCentavos = totalCentavos - baseCentavos * numParcelas;
      for (let i = 0; i < numParcelas; i++) {
        // Calcula a data da parcela manualmente para evitar problemas de fuso
        let mesParcela = Number(mesStr) - 1 + i;
        const anoParcela = Number(anoStr) + Math.floor(mesParcela / 12);
        mesParcela = mesParcela % 12;
        // Compra no dia 29/30/31 não pode gerar data inválida em meses mais curtos
        const ultimoDia = new Date(anoParcela, mesParcela + 1, 0).getDate();
        const diaParcela = Math.min(Number(diaStr), ultimoDia);
        const dataParcela = `${anoParcela}-${String(mesParcela + 1).padStart(2, '0')}-${String(diaParcela).padStart(2, '0')}`;
        lancs.push({
          categoria_id: categoriaId,
          valor: (baseCentavos + (i < restoCentavos ? 1 : 0)) / 100,
          data: dataParcela,
          descricao: `${descricao ? descricao + ' ' : ''}Parcela ${i + 1}/${numParcelas}`.trim(),
          tipo_lancamento: tipoLancamento,
          cartao_id: usarCartao ? cartaoId : null,
          banco_id: !usarCartao ? bancoId : null,
        });
      }
    } else if (isTransferencia) {
      // Saída do banco de origem + entrada no destino, com os tipos transf_*:
      // movem os saldos mas ficam fora dos totais de receita/despesa
      lancs.push({
        valor: valorNum,
        data,
        descricao: `${descricao ? descricao + ' ' : ''}🔁 Transferência para ${bancos.find(b => b.id === bancoDestinoId)?.nome}`.trim(),
        tipo_lancamento: 'transf_saida',
        banco_id: bancoId,
      });
      lancs.push({
        valor: valorNum,
        data,
        descricao: `${descricao ? descricao + ' ' : ''}🔁 Transferência de ${bancos.find(b => b.id === bancoId)?.nome}`.trim(),
        tipo_lancamento: 'transf_entrada',
        banco_id: bancoDestinoId,
      });
    } else {
      // Lançamento normal
      lancs.push({
        categoria_id: categoriaId,
        valor: valorNum,
        data,
        descricao,
        tipo_lancamento: tipoLancamento,
        cartao_id: usarCartao ? cartaoId : null,
        banco_id: !usarCartao ? bancoId : null,
      });
    }
    try {
      await api.post('/api/lancamentos', lancs);
      toast({ title: 'Lançamento salvo!', status: 'success' });
      setValor('');
      setDescricao('');
      setCategoriaId('');
      setUsarCartao(false);
      setCartaoId('');
      setBancoId('');
      setBancoDestinoId('');
      setParcelado(false);
      setNumParcelas(2);
      setTipoLancamento('despesa');
      await fetchLancamentos();
    } catch (err) {
      toast({ title: 'Erro ao salvar lançamento', description: err instanceof Error ? err.message : undefined, status: 'error' });
    }
    setLoading(false);
  };

  // Excluir lançamento
  const handleExcluir = async (id: string) => {
    setLoading(true);
    try {
      await api.delete(`/api/lancamentos/${id}`);
      setLancamentos(lancamentos.filter(l => l.id !== id));
      toast({ title: 'Lançamento excluído!', status: 'success' });
    } catch (err) {
      toast({ title: 'Erro ao excluir', description: err instanceof Error ? err.message : undefined, status: 'error' });
    }
    setLoading(false);
  };

  // Contas ativas ainda sem baixa no mês do lançamento
  const contasPendentesPara = (lanc: any) => contasFixas.filter((c: any) =>
    c.ativo !== false
    && !pagamentosContas.some((p: any) => p.conta_id === c.id && p.mes === lanc.mes && p.ano === lanc.ano));

  const abrirVinculo = (lanc: any) => {
    setVincularLanc(lanc);
    // Sugere a conta de mesmo valor, quando houver
    const igual = contasPendentesPara(lanc).find((c: any) => Math.abs(Number(c.valor) - Number(lanc.valor)) < 0.01);
    setVincularContaId(igual?.id || '');
  };

  const recarregarContas = async () => {
    const [cps, pgs] = await Promise.all([
      api.get('/api/contas-pagar'),
      api.get(`/api/contas-pagar/pagamentos?desde_ano=${new Date().getFullYear() - 1}`),
    ]);
    setContasFixas(cps);
    setPagamentosContas(pgs);
  };

  const confirmarVinculo = async () => {
    if (!vincularLanc || !vincularContaId) return;
    setLoading(true);
    try {
      await api.post(`/api/contas-pagar/${vincularContaId}/vincular`, { lancamento_id: vincularLanc.id });
      toast({ title: 'Conta marcada como paga com este lançamento!', status: 'success' });
      setVincularLanc(null);
      setVincularContaId('');
      await recarregarContas();
      await fetchLancamentos();
    } catch (err) {
      toast({ title: 'Erro ao vincular', description: err instanceof Error ? err.message : undefined, status: 'error' });
    }
    setLoading(false);
  };

  // Compra parcelada que o banco não numerou: projeta as parcelas que faltam
  // nas faturas seguintes, pro planejamento dos próximos meses já contar
  const confirmarParcelamento = async () => {
    if (!parcelarLanc) return;
    setLoading(true);
    try {
      const r = await api.post(`/api/lancamentos/${parcelarLanc.id}/parcelar`, {
        total: parcelarTotal,
        numero: parcelarNumero,
      });
      toast({
        title: r.parcelas_criadas
          ? `${r.parcelas_criadas} parcela(s) lançada(s) nos próximos meses`
          : 'Parcelas já estavam lançadas',
        status: 'success',
      });
      setParcelarLanc(null);
      await fetchLancamentos();
    } catch (err) {
      toast({ title: 'Erro ao parcelar', description: err instanceof Error ? err.message : undefined, status: 'error' });
    }
    setLoading(false);
  };

  const abrirParcelamento = (lanc: any) => {
    setParcelarLanc(lanc);
    setParcelarTotal(2);
    setParcelarNumero(1);
  };

  const alternarSelecao = (id: string) => {
    setSelecionados(sel => (sel.includes(id) ? sel.filter(x => x !== id) : [...sel, id]));
  };

  // Lançamento em conta, já ocorrido e sem id externo = o banco não confirmou
  // (candidato a duplicata manual ou a valor/data divergente do extrato)
  const semParNoExtrato = (l: any) =>
    !l.externo_id && (l.banco_id || l.cartao_id) && String(l.data).slice(0, 10) <= dataHojeLocal();

  const lancamentosFiltrados = lancamentos
    .filter((l: any) => {
      if (filtroRevisar && !l.revisar) return false;
      if (filtroSemPar && !semParNoExtrato(l)) return false;
      if (filtroTipo && l.tipo_lancamento !== filtroTipo) return false;
      if (filtroCategoria === 'sem' && l.categoria_id) return false;
      if (filtroCategoria && filtroCategoria !== 'sem' && l.categoria_id !== filtroCategoria) return false;
      if (filtroConta === 'sem' && (l.banco_id || l.cartao_id)) return false;
      if (filtroConta.startsWith('b:') && l.banco_id !== filtroConta.slice(2)) return false;
      if (filtroConta.startsWith('c:') && l.cartao_id !== filtroConta.slice(2)) return false;
      if (filtroTexto.trim()) {
        const alvo = `${l.descricao || ''} ${l.categoria?.nome || ''}`.toLowerCase();
        if (!alvo.includes(filtroTexto.trim().toLowerCase())) return false;
      }
      return true;
    })
    .sort((a: any, b: any) => {
      const sinal = ordemAsc ? 1 : -1;
      if (ordenarPor === 'valor') return sinal * (Number(a.valor) - Number(b.valor));
      if (ordenarPor === 'categoria') {
        return sinal * String(a.categoria?.nome || '').localeCompare(String(b.categoria?.nome || ''), 'pt-BR');
      }
      if (ordenarPor === 'descricao') {
        // Prefixo [bot]/[pluggy] fora da comparação: o que importa é o nome
        const limpar = (x: string) => String(x || '').replace(/^\[(bot|pluggy)\]\s*/, '');
        return sinal * limpar(a.descricao).localeCompare(limpar(b.descricao), 'pt-BR');
      }
      return sinal * String(a.data).localeCompare(String(b.data));
    });

  const somaFiltrada = lancamentosFiltrados.reduce((acc: number, l: any) => acc + Number(l.valor), 0);

  const alternarOrdem = (campo: 'data' | 'valor' | 'categoria' | 'descricao') => {
    if (ordenarPor === campo) setOrdemAsc(v => !v);
    else {
      setOrdenarPor(campo);
      setOrdemAsc(campo === 'descricao' || campo === 'categoria');
    }
  };

  const seta = (campo: string) => (ordenarPor === campo ? (ordemAsc ? ' ▲' : ' ▼') : '');

  const limparFiltros = () => {
    setFiltroTexto('');
    setFiltroRevisar(false);
    setFiltroTipo('');
    setFiltroCategoria('');
    setFiltroConta('');
    setFiltroDataIni('');
    setFiltroDataFim('');
    setFiltroSemPar(false);
  };

  const temFiltro = !!(filtroTexto || filtroRevisar || filtroSemPar || filtroTipo || filtroCategoria || filtroConta || filtroDataIni || filtroDataFim);

  // Quantos filtros além da busca estão ligados (vai no botão "Filtrar")
  const nFiltros = [filtroTipo, filtroCategoria, filtroConta, filtroDataIni || filtroDataFim, filtroRevisar, filtroSemPar].filter(Boolean).length;

  // Filtros além da busca: em linha no computador, na gaveta "Filtrar" no celular
  const filtrosAvancados = (
    <>
      <Select size="sm" w={{ base: '100%', md: '160px' }} placeholder="Todos os tipos"
        value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}>
        <option value="receita">Receita</option>
        <option value="despesa">Despesa</option>
        <option value="repasse_entrada">Repasse recebido</option>
        <option value="repasse_saida">Repasse enviado</option>
        <option value="transf_entrada">Transf. recebida</option>
        <option value="transf_saida">Transf. enviada</option>
        <option value="pagamento_fatura">Pgto. de fatura</option>
      </Select>
      <Select size="sm" w={{ base: '100%', md: '180px' }} placeholder="Todas as categorias"
        value={filtroCategoria} onChange={e => setFiltroCategoria(e.target.value)}>
        <option value="sem">(sem categoria)</option>
        {categorias.map(c => <option value={c.id} key={c.id}>{c.nome}</option>)}
      </Select>
      <Select size="sm" w={{ base: '100%', md: '170px' }} placeholder="Todas as contas"
        value={filtroConta} onChange={e => setFiltroConta(e.target.value)}>
        <option value="sem">(sem conta)</option>
        {bancos.map(b => <option value={`b:${b.id}`} key={b.id}>🏦 {b.nome}</option>)}
        {cartoes.map(c => <option value={`c:${c.id}`} key={c.id}>💳 {c.nome}</option>)}
      </Select>
      {/* Em tela estreita os dois campos de data ocupam a linha inteira
          em vez de empurrar a página pro lado */}
      <Flex align="center" gap={1} wrap="wrap" w={{ base: '100%', md: 'auto' }}>
        <Text fontSize="sm" color="gray.500">de</Text>
        <Input size="sm" type="date" flex={{ base: '1 1 120px', md: '0 0 150px' }} minW={0} value={filtroDataIni} onChange={e => setFiltroDataIni(e.target.value)} />
        <Text fontSize="sm" color="gray.500">até</Text>
        <Input size="sm" type="date" flex={{ base: '1 1 120px', md: '0 0 150px' }} minW={0} value={filtroDataFim} onChange={e => setFiltroDataFim(e.target.value)} />
      </Flex>
      <Checkbox isChecked={filtroRevisar} onChange={e => setFiltroRevisar(e.target.checked)}>
        Só pendentes de revisão
      </Checkbox>
      <Checkbox isChecked={filtroSemPar} onChange={e => setFiltroSemPar(e.target.checked)}>
        Só os que não vieram no extrato
      </Checkbox>
    </>
  );

  const todosSelecionados = lancamentosFiltrados.length > 0
    && lancamentosFiltrados.every((l: any) => selecionados.includes(l.id));

  const nomeConta = (l: any) => (l.cartao_id
    ? cartoes.find(c => c.id === l.cartao_id)?.nome
    : bancos.find(b => b.id === l.banco_id)?.nome) || '';

  // Celular: cartões agrupados por dia (quando a ordem é por data). Tocar no
  // lançamento abre a edição numa gaveta
  const gruposMobile: { dia: string | null; itens: any[] }[] = [];
  for (const l of lancamentosFiltrados) {
    const dia = ordenarPor === 'data' ? String(l.data).slice(0, 10) : null;
    const ultimo = gruposMobile[gruposMobile.length - 1];
    if (ultimo && ultimo.dia === dia) ultimo.itens.push(l);
    else gruposMobile.push({ dia, itens: [l] });
  }

  const listaMobile = (
    <Box borderWidth="1px" borderRadius="lg" overflow="hidden">
      <Flex px={3} py={2} align="center" justify="space-between" bg="gray.50" borderBottomWidth="1px">
        <Checkbox
          isChecked={todosSelecionados}
          isIndeterminate={selecionados.length > 0 && !todosSelecionados}
          onChange={e => setSelecionados(e.target.checked ? lancamentosFiltrados.map((l: any) => l.id) : [])}
        >
          <Text fontSize="sm">Selecionar todos</Text>
        </Checkbox>
      </Flex>
      {lancamentosFiltrados.length === 0 && (
        <Text p={4} color="gray.500" fontSize="sm">Nenhum lançamento com esses filtros.</Text>
      )}
      {gruposMobile.map((g, gi) => (
        <Box key={g.dia || gi}>
          {g.dia && (
            <Text px={3} pt={3} pb={1} fontSize="xs" fontWeight={700} color="gray.500" textTransform="uppercase" letterSpacing="wide">
              {cabecalhoDia(g.dia)}
            </Text>
          )}
          {g.itens.map((l: any) => {
            const selecionado = selecionados.includes(l.id);
            const neutro = !['receita', 'despesa'].includes(l.tipo_lancamento);
            const detalhes = [
              neutro ? labelTipo(l.tipo_lancamento) : l.categoria?.nome || 'Sem categoria',
              nomeConta(l),
              l.data_compra ? `compra ${formatarDataBR(l.data_compra).slice(0, 5)}` : '',
              ordenarPor !== 'data' ? formatarDataBR(l.data).slice(0, 5) : '',
            ].filter(Boolean).join(' · ');
            return (
              <Flex key={l.id} align="center" gap={3} px={3} py={3} borderTopWidth="1px" borderColor="gray.100" bg={selecionado ? 'blue.50' : undefined}>
                <Checkbox isChecked={selecionado} onChange={() => alternarSelecao(l.id)} aria-label="Selecionar" />
                <Flex as="button" type="button" flex="1" minW={0} align="center" gap={3} textAlign="left" onClick={() => startEdit(l)}>
                  <Box flex="1" minW={0}>
                    <Text fontWeight={600} fontSize="sm" noOfLines={1}>{descricaoCurta(l)}</Text>
                    <Flex gap={1} align="center" mt="2px" minW={0}>
                      {l.revisar && <Tag size="sm" colorScheme="orange" flexShrink={0}>revisar</Tag>}
                      {semParNoExtrato(l) && <Tag size="sm" colorScheme="yellow" flexShrink={0}>sem extrato</Tag>}
                      <Text fontSize="xs" color={!neutro && !l.categoria ? 'orange.500' : 'gray.500'} noOfLines={1}>{detalhes}</Text>
                    </Flex>
                  </Box>
                  <Text fontWeight={700} fontSize="sm" whiteSpace="nowrap" color={corTextoTipo(l.tipo_lancamento)}>
                    {isEntrada(l.tipo_lancamento) ? '+' : ''}R$ {formatarMoeda(Number(l.valor))}
                  </Text>
                </Flex>
              </Flex>
            );
          })}
        </Box>
      ))}
    </Box>
  );

  // Ações da gaveta de edição usam o lançamento original (o editLanc tem o
  // valor já formatado como texto)
  const lancOriginal = () => lancamentos.find((l: any) => l.id === editId);

  const aplicarLote = async (campos: Record<string, unknown>, rotulo: string) => {
    if (!selecionados.length) return;
    setLoading(true);
    try {
      const r = await api.patch('/api/lancamentos/lote', { ids: selecionados, ...campos });
      toast({ title: `${r.alterados} lançamento(s): ${rotulo}`, status: 'success' });
      setSelecionados([]);
      setLoteCategoria('');
      setLoteTipo('');
      setLoteConta('');
      await fetchLancamentos();
    } catch (err) {
      toast({ title: 'Erro ao aplicar em lote', description: err instanceof Error ? err.message : undefined, status: 'error' });
    }
    setLoading(false);
  };

  const excluirLote = async () => {
    if (!selecionados.length) return;
    // eslint-disable-next-line no-restricted-globals
    if (!window.confirm(`Excluir ${selecionados.length} lançamento(s)? Não dá pra desfazer.`)) return;
    setLoading(true);
    try {
      const r = await api.post('/api/lancamentos/lote/excluir', { ids: selecionados });
      toast({ title: `${r.excluidos} lançamento(s) excluído(s)`, status: 'success' });
      setSelecionados([]);
      await fetchLancamentos();
    } catch (err) {
      toast({ title: 'Erro ao excluir em lote', description: err instanceof Error ? err.message : undefined, status: 'error' });
    }
    setLoading(false);
  };

  // Iniciar edição
  const startEdit = (lanc: any) => {
    setEditId(lanc.id);
    setEditLanc({
      ...lanc,
      valor: formatarMoedaInput(lanc.valor),
      // No cartão o campo editável é o dia da compra; a data do lançamento é
      // o vencimento da fatura, calculado pelo servidor
      data: (lanc.data_compra || lanc.data).slice(0, 10),
      categoria_id: lanc.categoria_id,
      descricao: lanc.descricao || '',
      tipo_lancamento: lanc.tipo_lancamento,
    });
  };

  // Cancelar edição
  const cancelEdit = () => {
    setEditId(null);
    setEditLanc(null);
  };

  // Salvar edição
  const handleSalvarEdit = async () => {
    if (!editLanc) return;
    const valorNum = moedaParaNumero(editLanc.valor);
    if (isNaN(valorNum) || valorNum <= 0) {
      toast({ title: 'Valor inválido', status: 'error' });
      return;
    }
    setLoading(true);
    try {
      // mes/ano são derivados da data pelo servidor
      await api.put(`/api/lancamentos/${editLanc.id}`, {
        valor: valorNum,
        data: editLanc.data,
        descricao: editLanc.descricao,
        categoria_id: editLanc.categoria_id,
        tipo_lancamento: editLanc.tipo_lancamento,
        banco_id: editLanc.banco_id || null,
        cartao_id: editLanc.cartao_id || null,
      });
      toast({ title: 'Lançamento atualizado!', status: 'success' });
      cancelEdit();
      await fetchLancamentos();
    } catch (err) {
      toast({ title: 'Erro ao atualizar', description: err instanceof Error ? err.message : undefined, status: 'error' });
    }
    setLoading(false);
  };

  // Bloquear navegação durante operações
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (loading) {
        e.preventDefault();
        e.returnValue = '';
        return '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [loading]);

  return (
    <Box maxW="1000px" mx="auto" mt={{ base: 4, md: 10 }} p={4} pb={{ base: 8, md: 0 }}>
      <Heading size="lg" mb={2} textAlign="center">
        Lançamentos
      </Heading>
      <Text textAlign="center" mb={{ base: 4, md: 6 }} color="gray.600">Registre suas receitas e despesas do mês</Text>
      <Flex justify="space-between" align="center" mb={6} gap={2}>
        <Flex gap={2}>
          <Select value={mes} onChange={e => setMes(Number(e.target.value))} w="auto">
            {meses.map((m, idx) => (
              <option value={idx} key={m}>{m}</option>
            ))}
          </Select>
          <Select value={ano} onChange={e => setAno(Number(e.target.value))} w="auto">
            {[ano-1, ano, ano+1].map(a => (
              <option value={a} key={a}>{a}</option>
            ))}
          </Select>
        </Flex>
      </Flex>
      {isMobile ? (
        <Accordion allowToggle defaultIndex={[]} mb={8}>
          <AccordionItem borderRadius="md" boxShadow="md" bg="gray.50">
            <AccordionButton>
              <Box flex="1" textAlign="left" fontWeight="semibold">Novo lançamento</Box>
              <AccordionIcon />
            </AccordionButton>
            <AccordionPanel pb={4}>
              <Flex gap={4} flexWrap="wrap" align="end">
                <FormControl w="150px">
                  <FormLabel>Tipo</FormLabel>
                  <Select value={tipoLancamento} onChange={e => handleTipoChange(e.target.value as 'receita' | 'despesa' | 'transferencia')}>
                    <option value="receita">Receita</option>
                    <option value="despesa">Despesa</option>
                    <option value="transferencia">Transferência entre bancos</option>
                  </Select>
                </FormControl>
                {!isTransferencia && (
                  <FormControl w="200px">
                    <FormLabel>Categoria</FormLabel>
                    <Select value={categoriaId} onChange={e => setCategoriaId(e.target.value)} placeholder="Selecione">
                      {categoriasFiltradas.map(cat => (
                        <option value={cat.id} key={cat.id}>{cat.nome}</option>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {!isTransferencia && (
                  <FormControl w="200px">
                    <FormLabel>Pagamento</FormLabel>
                    <Select value={tipoLancamento === 'despesa' && usarCartao ? 'cartao' : 'dinheiro'} onChange={e => {
                      setUsarCartao(e.target.value === 'cartao');
                      setCartaoId('');
                      setBancoId('');
                      if (e.target.value !== 'cartao') setParcelado(false);
                    }} isDisabled={tipoLancamento !== 'despesa'}>
                      <option value="dinheiro">Dinheiro/Conta</option>
                      <option value="cartao">Cartão de crédito</option>
                    </Select>
                  </FormControl>
                )}
                {!isTransferencia && tipoLancamento === 'despesa' && usarCartao && (
                  <FormControl w="200px">
                    <FormLabel>Cartão</FormLabel>
                    <Select value={cartaoId} onChange={e => setCartaoId(e.target.value)} placeholder="Selecione o cartão">
                      {cartoes.map(cartao => (
                        <option value={cartao.id} key={cartao.id}>{cartao.nome}</option>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {!isTransferencia && !usarCartao && (
                  <FormControl w="200px">
                    <FormLabel>Banco</FormLabel>
                    <Select value={bancoId} onChange={e => setBancoId(e.target.value)} placeholder="Selecione o banco" isRequired>
                      {bancos.map(banco => (
                        <option value={banco.id} key={banco.id}>{banco.nome}</option>
                      ))}
                    </Select>
                  </FormControl>
                )}
                {isTransferencia && (
                  <>
                    <FormControl w="200px">
                      <FormLabel>Banco de Origem</FormLabel>
                      <Select value={bancoId} onChange={e => setBancoId(e.target.value)} placeholder="Selecione o banco" isRequired>
                        {bancos.map(banco => (
                          <option value={banco.id} key={banco.id}>{banco.nome}</option>
                        ))}
                      </Select>
                    </FormControl>
                    <FormControl w="200px">
                      <FormLabel>Banco de Destino</FormLabel>
                      <Select value={bancoDestinoId} onChange={e => setBancoDestinoId(e.target.value)} placeholder="Selecione o banco" isRequired>
                        {bancos.filter(b => b.id !== bancoId).map(banco => (
                          <option value={banco.id} key={banco.id}>{banco.nome}</option>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
                {!isTransferencia && tipoLancamento === 'despesa' && usarCartao && (
                  <>
                    <FormControl w="120px">
                      <FormLabel>Parcelado?</FormLabel>
                      <Checkbox isChecked={parcelado} onChange={e => setParcelado(e.target.checked)}>
                        {/* vazio para manter alinhamento */}
                      </Checkbox>
                    </FormControl>
                    <FormControl w="120px">
                      <FormLabel>Nº de parcelas</FormLabel>
                      <Input type="number" min={2} max={36} value={numParcelas} onChange={e => setNumParcelas(Number(e.target.value))} isDisabled={!parcelado} />
                    </FormControl>
                  </>
                )}
                <FormControl w="150px">
                  <FormLabel>Valor</FormLabel>
                  <Input
                    type="text"
                    value={valor}
                    onChange={e => setValor(formatarMoedaInput(e.target.value))}
                    placeholder="0,00"
                    inputMode="decimal"
                    maxLength={17}
                    textAlign="right"
                  />
                </FormControl>
                <FormControl w="170px">
                  {/* No cartão o que se informa é o dia da compra: o
                      lançamento entra no vencimento da fatura */}
                  <FormLabel>{usarCartao ? 'Data da compra' : 'Data'}</FormLabel>
                  <Input type="date" value={data} onChange={e => setData(e.target.value)} />
                </FormControl>
                <FormControl w="200px">
                  <FormLabel>Descrição</FormLabel>
                  <Input value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Opcional" />
                </FormControl>
                <FormControl alignSelf="flex-end">
                  <Button colorScheme="teal" onClick={handleSalvar} isLoading={loading} minW="120px">Salvar</Button>
                </FormControl>
              </Flex>
            </AccordionPanel>
          </AccordionItem>
        </Accordion>
      ) : (
        <Box mb={8} p={4} borderRadius="md" boxShadow="md" bg="gray.50">
          <Heading size="sm" mb={4}>Novo lançamento</Heading>
          <Flex gap={4} flexWrap="wrap" align="end">
            <FormControl w="150px">
              <FormLabel>Tipo</FormLabel>
              <Select value={tipoLancamento} onChange={e => handleTipoChange(e.target.value as 'receita' | 'despesa' | 'transferencia')}>
                <option value="receita">Receita</option>
                <option value="despesa">Despesa</option>
                <option value="transferencia">Transferência entre bancos</option>
              </Select>
            </FormControl>
            {!isTransferencia && (
              <FormControl w="200px">
                <FormLabel>Categoria</FormLabel>
                <Select value={categoriaId} onChange={e => setCategoriaId(e.target.value)} placeholder="Selecione">
                  {categoriasFiltradas.map(cat => (
                    <option value={cat.id} key={cat.id}>{cat.nome}</option>
                  ))}
                </Select>
              </FormControl>
            )}
            {!isTransferencia && (
              <FormControl w="200px">
                <FormLabel>Pagamento</FormLabel>
                <Select value={tipoLancamento === 'despesa' && usarCartao ? 'cartao' : 'dinheiro'} onChange={e => {
                  setUsarCartao(e.target.value === 'cartao');
                  setCartaoId('');
                  setBancoId('');
                  if (e.target.value !== 'cartao') setParcelado(false);
                }} isDisabled={tipoLancamento !== 'despesa'}>
                  <option value="dinheiro">Dinheiro/Conta</option>
                  <option value="cartao">Cartão de crédito</option>
                </Select>
              </FormControl>
            )}
            {!isTransferencia && tipoLancamento === 'despesa' && usarCartao && (
              <FormControl w="200px">
                <FormLabel>Cartão</FormLabel>
                <Select value={cartaoId} onChange={e => setCartaoId(e.target.value)} placeholder="Selecione o cartão">
                  {cartoes.map(cartao => (
                    <option value={cartao.id} key={cartao.id}>{cartao.nome}</option>
                  ))}
                </Select>
              </FormControl>
            )}
            {!isTransferencia && !usarCartao && (
              <FormControl w="200px">
                <FormLabel>Banco</FormLabel>
                <Select value={bancoId} onChange={e => setBancoId(e.target.value)} placeholder="Selecione o banco" isRequired>
                  {bancos.map(banco => (
                    <option value={banco.id} key={banco.id}>{banco.nome}</option>
                  ))}
                </Select>
              </FormControl>
            )}
            {isTransferencia && (
              <>
                <FormControl w="200px">
                  <FormLabel>Banco de Origem</FormLabel>
                  <Select value={bancoId} onChange={e => setBancoId(e.target.value)} placeholder="Selecione o banco" isRequired>
                    {bancos.map(banco => (
                      <option value={banco.id} key={banco.id}>{banco.nome}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl w="200px">
                  <FormLabel>Banco de Destino</FormLabel>
                  <Select value={bancoDestinoId} onChange={e => setBancoDestinoId(e.target.value)} placeholder="Selecione o banco" isRequired>
                    {bancos.filter(b => b.id !== bancoId).map(banco => (
                      <option value={banco.id} key={banco.id}>{banco.nome}</option>
                    ))}
                  </Select>
                </FormControl>
              </>
            )}
            {!isTransferencia && tipoLancamento === 'despesa' && usarCartao && (
              <>
                <FormControl w="120px">
                  <FormLabel>Parcelado?</FormLabel>
                  <Checkbox isChecked={parcelado} onChange={e => setParcelado(e.target.checked)}>
                    {/* vazio para manter alinhamento */}
                  </Checkbox>
                </FormControl>
                <FormControl w="120px">
                  <FormLabel>Nº de parcelas</FormLabel>
                  <Input type="number" min={2} max={36} value={numParcelas} onChange={e => setNumParcelas(Number(e.target.value))} isDisabled={!parcelado} />
                </FormControl>
              </>
            )}
            <FormControl w="150px">
              <FormLabel>Valor</FormLabel>
              <Input
                type="text"
                value={valor}
                onChange={e => setValor(formatarMoedaInput(e.target.value))}
                placeholder="0,00"
                inputMode="decimal"
                maxLength={17}
                textAlign="right"
              />
            </FormControl>
            <FormControl w="170px">
              <FormLabel>{usarCartao ? 'Data da compra' : 'Data'}</FormLabel>
              <Input type="date" value={data} onChange={e => setData(e.target.value)} />
            </FormControl>
            <FormControl w="200px">
              <FormLabel>Descrição</FormLabel>
              <Input value={descricao} onChange={e => setDescricao(e.target.value)} placeholder="Opcional" />
            </FormControl>
            <FormControl alignSelf="flex-end">
              <Button colorScheme="teal" onClick={handleSalvar} isLoading={loading} minW="120px">Salvar</Button>
            </FormControl>
          </Flex>
        </Box>
      )}
      <Divider mb={4} />
      <Box>
        <Heading size="md" mb={2}>Lançamentos do mês</Heading>
        {lancamentos.some((l: any) => l.revisar) && (
          <Box mb={3} p={3} borderRadius="md" bg="orange.50" borderWidth="1px" borderColor="orange.200">
            <Text fontSize="sm">
              ⏰ <b>{lancamentos.filter((l: any) => l.revisar).length}</b> lançamento(s) entraram sem
              confirmação no Telegram e estão marcados como <Tag colorScheme="orange" size="sm">revisar</Tag> —
              confira categoria e conta (editar e salvar limpa a marca).
            </Text>
          </Box>
        )}
        {isMobile ? (
          <Flex mb={3} gap={2} align="center">
            <Input
              size="md"
              flex="1"
              placeholder="Buscar"
              value={filtroTexto}
              onChange={e => setFiltroTexto(e.target.value)}
            />
            <Button onClick={filtrosMobile.onOpen} variant={nFiltros ? 'solid' : 'outline'} colorScheme={nFiltros ? 'teal' : undefined} flexShrink={0}>
              Filtrar{nFiltros ? ` (${nFiltros})` : ''}
            </Button>
          </Flex>
        ) : (
          <Flex mb={3} gap={3} align="center" wrap="wrap">
            <Input
              size="sm"
              w="260px"
              placeholder="Buscar na descrição/categoria"
              value={filtroTexto}
              onChange={e => setFiltroTexto(e.target.value)}
            />
            {filtrosAvancados}
            {temFiltro && (
              <Button size="sm" variant="ghost" onClick={limparFiltros}>Limpar filtros</Button>
            )}
          </Flex>
        )}
        <Flex mb={3} justify="space-between" align="center" gap={2}>
          <Text fontSize="sm" color="gray.500">
            {lancamentosFiltrados.length} de {lancamentos.length} lançamento(s) · soma R$ {formatarMoeda(somaFiltrada)}
          </Text>
          {isMobile && temFiltro && (
            <Button size="xs" variant="ghost" onClick={limparFiltros} flexShrink={0}>Limpar</Button>
          )}
        </Flex>
        {(filtroDataIni || filtroDataFim) && (
          <Text fontSize="xs" color="gray.500" mb={2}>
            Período informado: a lista ignora o mês selecionado acima.
          </Text>
        )}
        {selecionados.length > 0 && (
          <Box mb={3} p={3} borderRadius="md" bg="blue.50" borderWidth="1px" borderColor="blue.200">
            <Flex align="center" wrap="wrap" gap={2}>
              <Text fontWeight="bold" mr={1}>{selecionados.length} selecionado(s):</Text>
              <Select
                size="sm"
                w={{ base: '100%', md: '200px' }}
                placeholder="Definir categoria"
                value={loteCategoria}
                onChange={e => {
                  setLoteCategoria(e.target.value);
                  if (e.target.value) aplicarLote({ categoria_id: e.target.value, revisar: false }, 'categoria definida');
                }}
              >
                {categorias.filter(c => c.ativo !== false).map(c => (
                  <option value={c.id} key={c.id}>{c.nome} ({c.tipo_lancamento})</option>
                ))}
              </Select>
              <Select
                size="sm"
                w={{ base: '100%', md: '190px' }}
                placeholder="Definir tipo"
                value={loteTipo}
                onChange={e => {
                  setLoteTipo(e.target.value);
                  if (e.target.value) aplicarLote({ tipo_lancamento: e.target.value, revisar: false }, 'tipo definido');
                }}
              >
                <option value="receita">Receita</option>
                <option value="despesa">Despesa</option>
                <option value="repasse_entrada">Repasse recebido</option>
                <option value="repasse_saida">Repasse enviado</option>
                <option value="transf_entrada">Transf. recebida</option>
                <option value="transf_saida">Transf. enviada</option>
                <option value="pagamento_fatura">Pgto. de fatura</option>
              </Select>
              <Select
                size="sm"
                w={{ base: '100%', md: '190px' }}
                placeholder="Definir conta"
                value={loteConta}
                onChange={e => {
                  const v = e.target.value;
                  setLoteConta(v);
                  if (v.startsWith('b:')) aplicarLote({ banco_id: v.slice(2) }, 'banco definido');
                  else if (v.startsWith('c:')) aplicarLote({ cartao_id: v.slice(2) }, 'cartão definido');
                }}
              >
                {bancos.map(b => <option value={`b:${b.id}`} key={b.id}>🏦 {b.nome}</option>)}
                {cartoes.map(c => <option value={`c:${c.id}`} key={c.id}>💳 {c.nome}</option>)}
              </Select>
              <Button size="sm" onClick={() => aplicarLote({ revisar: false }, 'marcados como revisados')}>
                Marcar revisado
              </Button>
              <Button size="sm" colorScheme="red" variant="outline" onClick={excluirLote}>
                Excluir
              </Button>
              <Button size="sm" variant="ghost" onClick={() => setSelecionados([])}>
                Limpar seleção
              </Button>
            </Flex>
          </Box>
        )}
        {loading ? (
          <Spinner />
        ) : isMobile ? (
          listaMobile
        ) : (
          <Box overflowX="auto" maxW="100%">
            {/* Tabela larga rola dentro da própria caixa: no celular a página inteira não desliza pro lado */}
            <Table size="sm" variant="simple">
              <Thead>
                <Tr>
                  <Th px={1}>
                    <Checkbox
                      isChecked={todosSelecionados}
                      isIndeterminate={selecionados.length > 0 && !todosSelecionados}
                      onChange={e => setSelecionados(e.target.checked ? lancamentosFiltrados.map((l: any) => l.id) : [])}
                    />
                  </Th>
                  <Th cursor="pointer" onClick={() => alternarOrdem('data')}>Data{seta('data')}</Th>
                  {!isMobile && <Th>Tipo</Th>}
                  <Th cursor="pointer" onClick={() => alternarOrdem('categoria')}>Categoria{seta('categoria')}</Th>
                  {!isMobile && <Th>Tipo da Categoria</Th>}
                  {!isMobile && <Th>Banco/Cartão</Th>}
                  <Th isNumeric cursor="pointer" onClick={() => alternarOrdem('valor')}>Valor{seta('valor')}</Th>
                  {!isMobile && (
                    <Th cursor="pointer" onClick={() => alternarOrdem('descricao')}>Descrição{seta('descricao')}</Th>
                  )}
                  {!isMobile && <Th>Ações</Th>}
                  {!isMobile && <Th>Parcelas</Th>}
                </Tr>
              </Thead>
              <Tbody>
                {lancamentosFiltrados.map(lanc => (
                  <Tr key={lanc.id} bg={selecionados.includes(lanc.id) ? 'blue.50' : undefined}>
                    <Td px={1}>
                      <Checkbox
                        isChecked={selecionados.includes(lanc.id)}
                        onChange={() => alternarSelecao(lanc.id)}
                      />
                    </Td>
                    {editId === lanc.id ? (
                      <>
                        <Td>
                          <Input type="date" size="sm" value={editLanc.data} onChange={e => setEditLanc({ ...editLanc, data: e.target.value })} />
                        </Td>
                        {!isMobile && (
                          <Td>
                            <Tag colorScheme={corTipo(editLanc.tipo_lancamento)}>
                              {labelTipo(editLanc.tipo_lancamento)}
                            </Tag>
                          </Td>
                        )}
                        <Td>
                          <Select size="sm" value={editLanc.categoria_id} onChange={e => setEditLanc({ ...editLanc, categoria_id: e.target.value })}>
                            {categorias.filter(cat => cat.tipo_lancamento === editLanc.tipo_lancamento).map(cat => (
                              <option value={cat.id} key={cat.id}>{cat.nome}</option>
                            ))}
                          </Select>
                        </Td>
                        {!isMobile && (
                          <Td>
                            {(() => {
                              const cat = categorias.find(c => c.id === editLanc.categoria_id);
                              return cat && cat.tipo ? (
                                <Tag colorScheme={
                                  cat.tipo === 'essencial' ? 'green' :
                                  cat.tipo === 'superfluo' ? 'orange' :
                                  cat.tipo === 'investimento' ? 'blue' : 'gray'
                                }>
                                  {cat.tipo.charAt(0).toUpperCase() + cat.tipo.slice(1)}
                                </Tag>
                              ) : (
                                <Tag colorScheme="gray" variant="subtle">-</Tag>
                              );
                            })()}
                          </Td>
                        )}
                        {!isMobile && (
                          <Td>
                            {/* Lista bancos E cartões: permite mover um lançamento
                                de cartão pra banco (ex: era débito, não crédito) */}
                            <Select
                              size="sm"
                              value={editLanc.cartao_id ? `c:${editLanc.cartao_id}` : editLanc.banco_id ? `b:${editLanc.banco_id}` : ''}
                              placeholder="Sem conta"
                              onChange={e => {
                                const v = e.target.value;
                                setEditLanc({
                                  ...editLanc,
                                  banco_id: v.startsWith('b:') ? v.slice(2) : null,
                                  cartao_id: v.startsWith('c:') ? v.slice(2) : null,
                                });
                              }}
                            >
                              {bancos.map(b => (
                                <option value={`b:${b.id}`} key={b.id}>🏦 {b.nome}</option>
                              ))}
                              {cartoes.map(c => (
                                <option value={`c:${c.id}`} key={c.id}>💳 {c.nome}</option>
                              ))}
                            </Select>
                          </Td>
                        )}
                        <Td isNumeric>
                          <Input size="sm" value={editLanc.valor} onChange={e => setEditLanc({ ...editLanc, valor: e.target.value })} />
                        </Td>
                        {!isMobile && (
                          <Td>
                            <Input size="sm" value={editLanc.descricao} onChange={e => setEditLanc({ ...editLanc, descricao: e.target.value })} />
                          </Td>
                        )}
                        {!isMobile && (
                          <Td>
                            <IconButton aria-label="Salvar" icon={<CheckIcon />} size="sm" colorScheme="teal" variant="ghost" onClick={handleSalvarEdit} />
                            <IconButton aria-label="Cancelar" icon={<CloseIcon />} size="sm" colorScheme="gray" variant="ghost" onClick={cancelEdit} />
                          </Td>
                        )}
                        {!isMobile && (
                          <Td>
                            {editLanc.cartao_id ? getParcelaInfo(editLanc) : '-'}
                          </Td>
                        )}
                      </>
                    ) : (
                      <>
                        <Td whiteSpace="nowrap">
                          {formatarDataBR(lanc.data)}
                          {lanc.data_compra && (
                            <Text fontSize="xs" color="gray.500">
                              compra {formatarDataBR(lanc.data_compra).slice(0, 5)}
                            </Text>
                          )}
                        </Td>
                        {!isMobile && (
                          <Td>
                            <Tag colorScheme={corTipo(lanc.tipo_lancamento)}>
                              {labelTipo(lanc.tipo_lancamento)}
                            </Tag>
                          </Td>
                        )}
                        <Td>
                          {lanc.revisar && <Tag colorScheme="orange" size="sm" mr={1}>revisar</Tag>}
                          {semParNoExtrato(lanc) && <Tag colorScheme="yellow" size="sm" mr={1} title="Lançado à mão e ainda não apareceu no extrato do banco">sem extrato</Tag>}
                          {lanc.categoria?.nome || '-'}
                        </Td>
                        {!isMobile && (
                          <Td>
                            {lanc.categoria?.tipo ? (
                              <Tag colorScheme={
                                lanc.categoria.tipo === 'essencial' ? 'green' :
                                lanc.categoria.tipo === 'superfluo' ? 'orange' :
                                lanc.categoria.tipo === 'investimento' ? 'blue' : 'gray'
                              }>
                                {lanc.categoria.tipo.charAt(0).toUpperCase() + lanc.categoria.tipo.slice(1)}
                              </Tag>
                            ) : (
                              <Tag colorScheme="gray" variant="subtle">-</Tag>
                            )}
                          </Td>
                        )}
                        {!isMobile && (
                          <Td>{lanc.cartao_id ? cartoes.find(c => c.id === lanc.cartao_id)?.nome || '-' : bancos.find(b => b.id === lanc.banco_id)?.nome || '-'}</Td>
                        )}
                        <Td isNumeric color={corTextoTipo(lanc.tipo_lancamento)} whiteSpace={{ base: 'normal', md: 'nowrap' }}>
                          R$ {formatarMoeda(Number(lanc.valor))}
                        </Td>
                        {!isMobile && (
                          <Td>{lanc.descricao || '-'}</Td>
                        )}
                        {!isMobile && (
                          <Td>
                            <IconButton aria-label="Editar" icon={<EditIcon />} size="sm" variant="ghost" onClick={() => startEdit(lanc)} mr={1} />
                            {['despesa', 'pagamento_fatura'].includes(lanc.tipo_lancamento) && (
                              <Button
                                size="xs"
                                variant="ghost"
                                title="Marcar uma conta fixa como paga por este lançamento"
                                onClick={() => abrirVinculo(lanc)}
                                mr={1}
                              >
                                📋
                              </Button>
                            )}
                            {lanc.tipo_lancamento === 'despesa' && (
                              <Button
                                size="xs"
                                variant="ghost"
                                title="É parcela de uma compra parcelada: lançar as parcelas que faltam"
                                onClick={() => abrirParcelamento(lanc)}
                                mr={1}
                              >
                                🔢
                              </Button>
                            )}
                            <IconButton aria-label="Excluir" icon={<DeleteIcon />} size="sm" colorScheme="red" variant="ghost" onClick={() => setDeleteId(lanc.id)} />
                          </Td>
                        )}
                        {!isMobile && (
                          <Td>
                            {lanc.cartao_id ? getParcelaInfo(lanc) : '-'}
                          </Td>
                        )}
                      </>
                    )}
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </Box>
        )}
      </Box>
      {/* Celular: filtros e ordem numa gaveta, para a lista aparecer logo */}
      <Drawer placement="bottom" isOpen={filtrosMobile.isOpen} onClose={filtrosMobile.onClose}>
        <DrawerOverlay />
        <DrawerContent borderTopRadius="2xl" maxH="90vh">
          <DrawerCloseButton />
          <DrawerHeader pb={2}>Filtrar e ordenar</DrawerHeader>
          <DrawerBody>
            <Stack spacing={3}>
              <FormControl>
                <FormLabel fontSize="sm" mb={1}>Ordenar por</FormLabel>
                <Select
                  size="sm"
                  value={`${ordenarPor}:${ordemAsc ? 'asc' : 'desc'}`}
                  onChange={e => {
                    const [campo, dir] = e.target.value.split(':');
                    setOrdenarPor(campo as any);
                    setOrdemAsc(dir === 'asc');
                  }}
                >
                  <option value="data:desc">Data (mais recente primeiro)</option>
                  <option value="data:asc">Data (mais antiga primeiro)</option>
                  <option value="valor:desc">Valor (maior primeiro)</option>
                  <option value="valor:asc">Valor (menor primeiro)</option>
                  <option value="categoria:asc">Categoria (A-Z)</option>
                  <option value="descricao:asc">Descrição (A-Z)</option>
                </Select>
              </FormControl>
              {filtrosAvancados}
            </Stack>
          </DrawerBody>
          <DrawerFooter gap={3} pb="calc(16px + env(safe-area-inset-bottom))">
            <Button variant="ghost" onClick={limparFiltros} isDisabled={!temFiltro}>Limpar</Button>
            <Button colorScheme="teal" flex="1" onClick={filtrosMobile.onClose}>
              Ver {lancamentosFiltrados.length} lançamento(s)
            </Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Celular: edição do lançamento tocado */}
      <Drawer placement="bottom" isOpen={!!isMobile && !!editId} onClose={cancelEdit}>
        <DrawerOverlay />
        <DrawerContent borderTopRadius="2xl" maxH="92vh">
          <DrawerCloseButton />
          <DrawerHeader pb={1}>Editar lançamento</DrawerHeader>
          <DrawerBody>
            {editLanc && (
              <Stack spacing={3}>
                <Flex gap={2} align="center" wrap="wrap">
                  <Tag colorScheme={corTipo(editLanc.tipo_lancamento)}>{labelTipo(editLanc.tipo_lancamento)}</Tag>
                  {editLanc.cartao_id && getParcelaInfo(editLanc) !== '-' && (
                    <Tag variant="outline">Parcela {getParcelaInfo(editLanc)}</Tag>
                  )}
                  {editLanc.revisar && <Tag colorScheme="orange">revisar: salvar confirma</Tag>}
                </Flex>
                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>Descrição</FormLabel>
                  <Input value={editLanc.descricao} onChange={e => setEditLanc({ ...editLanc, descricao: e.target.value })} />
                </FormControl>
                <Flex gap={3}>
                  <FormControl>
                    <FormLabel fontSize="sm" mb={1}>Valor (R$)</FormLabel>
                    <Input
                      value={editLanc.valor}
                      inputMode="decimal"
                      textAlign="right"
                      onChange={e => setEditLanc({ ...editLanc, valor: formatarMoedaInput(e.target.value) })}
                    />
                  </FormControl>
                  <FormControl>
                    <FormLabel fontSize="sm" mb={1}>{editLanc.cartao_id ? 'Data da compra' : 'Data'}</FormLabel>
                    <Input type="date" value={editLanc.data} onChange={e => setEditLanc({ ...editLanc, data: e.target.value })} />
                  </FormControl>
                </Flex>
                {categorias.some(cat => cat.tipo_lancamento === editLanc.tipo_lancamento) && (
                  <FormControl>
                    <FormLabel fontSize="sm" mb={1}>Categoria</FormLabel>
                    <Select
                      placeholder="Sem categoria"
                      value={editLanc.categoria_id || ''}
                      onChange={e => setEditLanc({ ...editLanc, categoria_id: e.target.value || null })}
                    >
                      {categorias.filter(cat => cat.tipo_lancamento === editLanc.tipo_lancamento && (cat.ativo !== false || cat.id === editLanc.categoria_id)).map(cat => (
                        <option value={cat.id} key={cat.id}>{cat.nome}</option>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <FormControl>
                  <FormLabel fontSize="sm" mb={1}>Banco ou cartão</FormLabel>
                  <Select
                    placeholder="Sem conta"
                    value={editLanc.cartao_id ? `c:${editLanc.cartao_id}` : editLanc.banco_id ? `b:${editLanc.banco_id}` : ''}
                    onChange={e => {
                      const v = e.target.value;
                      setEditLanc({
                        ...editLanc,
                        banco_id: v.startsWith('b:') ? v.slice(2) : null,
                        cartao_id: v.startsWith('c:') ? v.slice(2) : null,
                      });
                    }}
                  >
                    {bancos.map(b => <option value={`b:${b.id}`} key={b.id}>🏦 {b.nome}</option>)}
                    {cartoes.map(c => <option value={`c:${c.id}`} key={c.id}>💳 {c.nome}</option>)}
                  </Select>
                </FormControl>
                <Stack spacing={2} pt={2}>
                  {['despesa', 'pagamento_fatura'].includes(editLanc.tipo_lancamento) && (
                    <Button variant="outline" size="sm" justifyContent="flex-start" onClick={() => { const l = lancOriginal(); cancelEdit(); if (l) abrirVinculo(l); }}>
                      📋 Marcar uma conta fixa como paga por este
                    </Button>
                  )}
                  {editLanc.tipo_lancamento === 'despesa' && (
                    <Button variant="outline" size="sm" justifyContent="flex-start" onClick={() => { const l = lancOriginal(); cancelEdit(); if (l) abrirParcelamento(l); }}>
                      🔢 É parcela de uma compra parcelada
                    </Button>
                  )}
                  <Button variant="outline" size="sm" colorScheme="red" justifyContent="flex-start" leftIcon={<DeleteIcon />} onClick={() => { const id = editId; cancelEdit(); setDeleteId(id); }}>
                    Excluir lançamento
                  </Button>
                </Stack>
              </Stack>
            )}
          </DrawerBody>
          <DrawerFooter gap={3} pb="calc(16px + env(safe-area-inset-bottom))">
            <Button variant="outline" flex="1" onClick={cancelEdit}>Cancelar</Button>
            <Button colorScheme="teal" flex="1" onClick={handleSalvarEdit} isLoading={loading}>Salvar</Button>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>

      {/* Compra parcelada que o banco não numerou */}
      <AlertDialog isOpen={!!parcelarLanc} leastDestructiveRef={cancelRef} onClose={() => setParcelarLanc(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Compra parcelada</AlertDialogHeader>
            <AlertDialogBody>
              <Text fontSize="sm" mb={3}>
                R$ {parcelarLanc ? formatarMoeda(Number(parcelarLanc.valor)) : ''} ·{' '}
                {parcelarLanc ? formatarDataBR(String(parcelarLanc.data)) : ''}
                <br />
                {parcelarLanc?.descricao || ''}
              </Text>
              <Flex gap={3}>
                <FormControl>
                  <FormLabel>Total de parcelas</FormLabel>
                  <Input
                    type="number"
                    min={2}
                    max={48}
                    value={parcelarTotal}
                    onChange={e => setParcelarTotal(Number(e.target.value))}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel>Esta é a parcela</FormLabel>
                  <Input
                    type="number"
                    min={1}
                    max={parcelarTotal}
                    value={parcelarNumero}
                    onChange={e => setParcelarNumero(Number(e.target.value))}
                  />
                </FormControl>
              </Flex>
              <Text fontSize="sm" color="gray.500" mt={3}>
                O valor de cada parcela é o deste lançamento. As parcelas que faltam
                entram uma por mês nas faturas seguintes, para o planejamento dos
                próximos meses já contar com elas. Rodar de novo não duplica.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setParcelarLanc(null)}>Cancelar</Button>
              <Button colorScheme="teal" ml={3} onClick={confirmarParcelamento} isLoading={loading}>
                Lançar parcelas
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Vincular lançamento existente à baixa de uma conta fixa */}
      <AlertDialog isOpen={!!vincularLanc} leastDestructiveRef={cancelRef} onClose={() => setVincularLanc(null)}>
        <AlertDialogOverlay>
          <AlertDialogContent mx={4}>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">Vincular a uma conta fixa</AlertDialogHeader>
            <AlertDialogBody>
              <Text fontSize="sm" mb={3}>
                R$ {vincularLanc ? formatarMoeda(Number(vincularLanc.valor)) : ''} ·{' '}
                {vincularLanc ? formatarDataBR(String(vincularLanc.data).slice(0, 10)) : ''}
                <br />
                {vincularLanc?.descricao || ''}
              </Text>
              <FormControl>
                <FormLabel>Conta a pagar (pendentes do mês)</FormLabel>
                <Select
                  placeholder="Selecione a conta"
                  value={vincularContaId}
                  onChange={e => setVincularContaId(e.target.value)}
                >
                  {vincularLanc && contasPendentesPara(vincularLanc).map((c: any) => (
                    <option value={c.id} key={c.id}>{c.descricao} (R$ {formatarMoeda(Number(c.valor))})</option>
                  ))}
                </Select>
              </FormControl>
              <Text fontSize="sm" color="gray.500" mt={3}>
                A conta é marcada como paga usando este lançamento — nada é criado
                nem duplicado, e a confirmação do banco é preservada. Sem categoria
                própria, ele herda a da conta.
              </Text>
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setVincularLanc(null)}>Cancelar</Button>
              <Button colorScheme="teal" ml={3} onClick={confirmarVinculo} isLoading={loading} isDisabled={!vincularContaId}>
                Vincular
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Popup de confirmação de exclusão */}
      <AlertDialog
        isOpen={!!deleteId}
        leastDestructiveRef={cancelRef}
        onClose={() => setDeleteId(null)}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirmar exclusão
            </AlertDialogHeader>
            <AlertDialogBody>
              Tem certeza que deseja deletar este lançamento? Essa ação não pode ser desfeita.
            </AlertDialogBody>
            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setDeleteId(null)}>
                Cancelar
              </Button>
              <Button colorScheme="red" onClick={() => { if(deleteId) handleExcluir(deleteId); setDeleteId(null); }} ml={3}>
                Deletar
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
};

export default Lancamentos; 